import { getJWTFromLocalStorage } from "../localStorage";

/**
 * POST /ai-agents/:id/chat/ with stream:true and consume text/event-stream (OpenAI-style SSE passthrough).
 * @param {object} opts
 * @param {number|string} opts.id - Agent id
 * @param {object[]} opts.messages - Chat messages
 * @param {(chunk: string, full: string) => void} [opts.onDelta]
 * @param {AbortSignal} [opts.signal] - Abort to cancel the request (e.g. saga teardown)
 * @returns {Promise<{ fullText: string, messageId: string | null }>}
 */
export function postAiAgentChatStream({ id, messages, onDelta, signal, save_data = true }) {
  const base = process.env.REACT_APP_API_URL || "";
  const token = getJWTFromLocalStorage();
  const url = `${base.replace(/\/$/, "")}/ai-agents/${id}/chat/`;

  return (async () => {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ messages, stream: true, save_data }),
      credentials: "include",
      signal,
    });

    if (!res.ok) {
      const t = await res.text();
      throw new Error(t || `HTTP ${res.status}`);
    }

    if (!res.body) {
      throw new Error("No response body (streaming not supported in this browser).");
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let fullText = "";
    let messageId = null;
    let pendingDelta = false;
    let rafId = null;

    const flushDelta = () => {
      rafId = null;
      if (!pendingDelta || !onDelta) return;
      pendingDelta = false;
      onDelta("", fullText);
    };

    const scheduleDelta = () => {
      pendingDelta = true;
      if (rafId == null) {
        rafId = requestAnimationFrame(flushDelta);
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (raw === "[DONE]") continue;
        let j;
        try {
          j = JSON.parse(raw);
        } catch {
          continue;
        }
        if (j.error) {
          throw new Error(
            typeof j.error === "string" ? j.error : JSON.stringify(j.error)
          );
        }
        if (j.done) {
          if (j.message_id) messageId = j.message_id;
          continue;
        }
        const choices = j.choices || [];
        for (const ch of choices) {
          const delta = ch.delta || {};
          const c = delta.content;
          if (c) {
            fullText += c;
            scheduleDelta();
          }
        }
      }
    }

    if (rafId != null) {
      cancelAnimationFrame(rafId);
      flushDelta();
    }

    return { fullText, messageId };
  })();
}
